import React from 'react';
import { Link } from 'react-router-dom';
import { useAdminEdit } from '../contexts/AdminEditContext';
import { useSubmitBusiness } from '../contexts/SubmitBusinessContext';
import EditableText from './EditableText';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, Send } from 'lucide-react';

const Footer = () => {
  const { saveCustomization, getCustomization } = useAdminEdit();
  const { openModal } = useSubmitBusiness();
  const [email, setEmail] = React.useState('');

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement newsletter subscription
    console.log('Newsletter subscription:', email);
    setEmail('');
  };

  return (
    <footer className="relative bg-gray-200/60 dark:bg-gray-950 text-gray-700 dark:text-gray-300 mt-auto border-t border-gray-300 dark:border-gray-800 font-sans">
      {/* Main Footer Content */}
      <div className="relative container-custom py-14 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">

          {/* Brand & Description Section */}
          <div className="space-y-6 lg:col-span-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="/digi_fatehabad_logo.png"
                  alt="Digital Fatehabad Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h2 className="text-gray-900 dark:text-white font-bold text-xl tracking-tight">
                <EditableText
                  value={getCustomization('footerBrandName', 'Digital Fatehabad')}
                  onSave={(value) => saveCustomization('footerBrandName', value)}
                  className="text-gray-900 dark:text-white"
                  tag="span"
                />
              </h2>
            </div>

            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 max-w-sm transition-colors hover:text-gray-800 dark:hover:text-gray-300">
              <EditableText
                value={getCustomization('footerDescription', 'Your trusted local business directory. Discover and connect with the best services in Fatehabad.')}
                onSave={(value) => saveCustomization('footerDescription', value)}
                className="text-gray-600 dark:text-gray-400"
                tag="span"
                multiline={true}
              />
            </p>

            {/* Social Media Links */}
            <div className="flex items-center gap-3">
              {[
                { Icon: Facebook, href: "https://facebook.com", color: "hover:bg-[#1877F2]" },
                { Icon: Twitter, href: "https://twitter.com", color: "hover:bg-[#1DA1F2]" },
                { Icon: Instagram, href: "https://instagram.com", color: "hover:bg-[#E4405F]" },
                { Icon: Linkedin, href: "https://linkedin.com", color: "hover:bg-[#0A66C2]" }
              ].map(({ Icon, href, color }, index) => (
                <a
                  key={index}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-9 h-9 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-center transition-all duration-300 hover:scale-110 hover:text-white group ${color}`}
                >
                  <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400 transition-colors group-hover:text-white" />
                </a>
              ))}
            </div>

            {/* Newsletter Card */}
            <div className="p-5 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200/80 dark:border-gray-800 shadow-sm max-w-sm">
              <h3 className="text-gray-900 dark:text-white font-bold mb-2 text-base">
                Newsletter
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3.5 leading-relaxed">
                Subscribe to get updates on new businesses and offers directly to your inbox.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full pl-3.5 pr-11 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 transition-all"
                  required
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1 p-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all shadow-sm"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            </div>
          </div>

          {/* Combined Navigation Section */}
          <div className="lg:col-span-4">
            <h3 className="text-gray-900 dark:text-white font-bold mb-6 text-base tracking-wide flex items-center gap-2">
              Explore
            </h3>
            <div className="grid grid-cols-2 gap-6 sm:gap-8">
              {/* Column 1 */}
              <ul className="space-y-3 sm:space-y-2.5">
                {[
                  { to: "/", label: "Home" },
                  { to: "/categories", label: "Categories" },
                  { to: "/villages", label: "Villages Directory" },
                  { to: "/bhavi-sarpanch", label: "भावी सरपंच" },
                  { to: "/advocates", label: "Advocates Directory" },
                  { to: "/blog", label: "Blog" },
                  { to: "/news", label: "News" },
                  { to: "/blood-donors", label: "Blood Donors" },
                  { to: "/bus-time-table", label: "Bus Time Table" },
                  { to: "/officers", label: "Officers Directory" },
                  { action: openModal, label: "Submit Business" }
                ].map((link, idx) => (
                  <li key={idx}>
                    {link.to ? (
                      <Link to={link.to} className="group flex items-center text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-white transition-all duration-200 hover:translate-x-1 py-0.5">
                        <span className="text-primary-500 mr-2.5 font-mono text-sm font-semibold select-none">&gt;</span>
                        <span>{link.label}</span>
                      </Link>
                    ) : (
                      <button onClick={link.action} className="group flex items-center text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-white transition-all duration-200 hover:translate-x-1 py-0.5 text-left">
                        <span className="text-primary-500 mr-2.5 font-mono text-sm font-semibold select-none">&gt;</span>
                        <span>{link.label}</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              {/* Column 2 */}
              <ul className="space-y-3 sm:space-y-2.5">
                {[
                  { to: "/about", label: "About Us" },
                  { to: "/how-it-works", label: "How It Works" },
                  { to: "/contact", label: "Contact Us" },
                  { to: "/faq", label: "FAQ" },
                  { to: "/privacy", label: "Privacy Policy" },
                  { to: "/terms", label: "Terms of Service" },
                  { to: "/admin/login", label: "Admin Login" }
                ].map((link, idx) => (
                  <li key={idx}>
                    <Link to={link.to} className="group flex items-center text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-white transition-all duration-200 hover:translate-x-1 py-0.5">
                      <span className="text-primary-500 mr-2.5 font-mono text-sm font-semibold select-none">&gt;</span>
                      <span>{link.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Section */}
          <div className="space-y-6 lg:col-span-4">
            <h3 className="text-gray-900 dark:text-white font-bold mb-6 text-base tracking-wide flex items-center gap-2">
              Contact Us
            </h3>
            <div className="space-y-3.5 pt-1">
              <div className="flex items-start gap-3 group">
                <div className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-primary-600 dark:text-primary-400 shadow-xs">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="text-xs sm:text-sm">
                  <p className="text-gray-900 dark:text-white font-medium">Head Office</p>
                  <span className="text-gray-500 dark:text-gray-400">Fatehabad, Haryana, India</span>
                </div>
              </div>
              <div className="flex items-center gap-3 group">
                <div className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-primary-600 dark:text-primary-400 shadow-xs">
                  <Phone className="w-4 h-4" />
                </div>
                <div className="text-xs sm:text-sm">
                  <p className="text-gray-900 dark:text-white font-medium">Phone</p>
                  <a href="tel:+919802250514" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-white transition-colors">
                    +91 98022 50514
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 group">
                <div className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-primary-600 dark:text-primary-400 shadow-xs">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="text-xs sm:text-sm">
                  <p className="text-gray-900 dark:text-white font-medium">Email</p>
                  <span className="text-gray-500 dark:text-gray-400">info@digitalfatehabad.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Section */}
      <div className="border-t border-gray-300 dark:border-gray-800 bg-gray-300/40 dark:bg-black/40">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs sm:text-sm">
            <p className="text-gray-500 dark:text-gray-400">
              © {new Date().getFullYear()} <span className="text-gray-800 dark:text-gray-200 font-medium">
                <EditableText
                  value={getCustomization('footerCopyrightBrand', 'Digital Fatehabad')}
                  onSave={(value) => saveCustomization('footerCopyrightBrand', value)}
                  className="text-gray-800 dark:text-gray-200 font-medium"
                  tag="span"
                />
              </span>. <EditableText
                value={getCustomization('footerCopyrightText', 'All rights reserved.')}
                onSave={(value) => saveCustomization('footerCopyrightText', value)}
                className="text-gray-500 dark:text-gray-400"
                tag="span"
              />
            </p>
            <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 px-3.5 py-1.5 rounded-full border border-gray-200 dark:border-gray-800 shadow-xs text-xs">
              <EditableText
                value={getCustomization('footerMadeWithText', 'Made with')}
                onSave={(value) => saveCustomization('footerMadeWithText', value)}
                className="text-gray-500 dark:text-gray-400"
                tag="span"
              />
              {' '}
              <span className="text-red-500 animate-pulse text-sm">❤️</span>
              {' '}
              <EditableText
                value={getCustomization('footerMadeForText', 'for Fatehabad')}
                onSave={(value) => saveCustomization('footerMadeForText', value)}
                className="text-gray-500 dark:text-gray-400"
                tag="span"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
